import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import './Header.css';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Services', to: '/services' },
  { label: 'Contact', to: '/contact' },
];

const INFO_LINKS = [
  { label: 'What happens to your scrap', to: '/what-happens-to-your-scrap' },
  { label: "FAQ's", to: '/faqs' },
];

const INFO_PATHS = INFO_LINKS.map((link) => link.to);

function ChevronIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M2.5 4.5L6 8l3.5-3.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MenuIcon({ open }) {
  if (open) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 7h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M4 12h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function NavArrowIcon() {
  return (
    <svg className="mobile-nav-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M6 3.5L10.5 8 6 12.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Header() {
  const { user, isLoggedIn, logout } = useAuth();
  const [infoOpen, setInfoOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const infoRef = useRef(null);
  const accountRef = useRef(null);
  const location = useLocation();
  const infoActive = INFO_PATHS.includes(location.pathname);

  useEffect(() => {
    function handleClickOutside(event) {
      if (infoRef.current && !infoRef.current.contains(event.target)) {
        setInfoOpen(false);
      }
      if (accountRef.current && !accountRef.current.contains(event.target)) {
        setAccountOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
    setInfoOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);

  function handleLogout() {
    logout();
    setAccountOpen(false);
    setMenuOpen(false);
  }

  return (
    <header className="site-header">
      <Link className="announcement-bar" to="/post-scrap">
        <span>Post Scrap or Become a Collector • Available 24/7 →</span>
      </Link>

      <div className="nav-bar">
        <div className="nav-inner">
          <Link className="brand" to="/" aria-label="SnapScrap home">
            SnapScrap
          </Link>

          <nav className="nav-links" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.label}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) => (isActive ? 'nav-link is-active' : 'nav-link')}
              >
                {link.label}
              </NavLink>
            ))}

            <div
              className={`nav-dropdown ${infoOpen ? 'is-open' : ''} ${infoActive ? 'is-active' : ''}`}
              ref={infoRef}
            >
              <button
                type="button"
                className={`nav-link nav-dropdown-trigger ${infoActive ? 'is-active' : ''}`}
                aria-expanded={infoOpen}
                aria-haspopup="true"
                onClick={() => setInfoOpen((open) => !open)}
              >
                Info
                <ChevronIcon />
              </button>
              <div className="nav-dropdown-menu" role="menu">
                {INFO_LINKS.map((link) => (
                  <NavLink
                    key={link.label}
                    to={link.to}
                    role="menuitem"
                    className={({ isActive }) => (isActive ? 'is-active' : undefined)}
                    onClick={() => setInfoOpen(false)}
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </nav>

          <div className="nav-actions">
            {isLoggedIn ? (
              <div className={`nav-dropdown account-dropdown ${accountOpen ? 'is-open' : ''}`} ref={accountRef}>
                <button
                  type="button"
                  className="header-btn header-btn-account"
                  aria-expanded={accountOpen}
                  aria-haspopup="true"
                  onClick={() => setAccountOpen((open) => !open)}
                >
                  {user.full_name?.split(' ')[0] || 'Account'}
                  <ChevronIcon />
                </button>
                <div className="nav-dropdown-menu account-menu" role="menu">
                  <div className="account-menu-meta">
                    <strong>{user.full_name}</strong>
                    <span>{user.email}</span>
                  </div>
                  <Link to="/post-scrap" role="menuitem" onClick={() => setAccountOpen(false)}>
                    Post Scrap
                  </Link>
                  <button type="button" role="menuitem" onClick={handleLogout}>
                    Log out
                  </button>
                </div>
              </div>
            ) : (
              <Link className="header-btn header-btn-login" to="/login">
                Log in
              </Link>
            )}
            <Link className="header-btn header-btn-primary" to="/post-scrap">
              Post Scrap
            </Link>
            <Link className="header-btn header-btn-secondary" to="/become-a-collector">
              Become a Collector
            </Link>
            <button
              type="button"
              className="menu-toggle"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <MenuIcon open={menuOpen} />
            </button>
          </div>
        </div>
      </div>

      <div className={`mobile-menu ${menuOpen ? 'is-open' : ''}`} id="mobile-navigation">
        <nav className="mobile-menu-panel" aria-label="Mobile">
          <div className="mobile-menu-links">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.label}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) =>
                  isActive ? 'mobile-nav-link is-active' : 'mobile-nav-link'
                }
                onClick={() => setMenuOpen(false)}
              >
                <span>{link.label}</span>
                <NavArrowIcon />
              </NavLink>
            ))}
            {INFO_LINKS.map((link) => (
              <NavLink
                key={link.label}
                to={link.to}
                className={({ isActive }) =>
                  isActive ? 'mobile-nav-link is-active' : 'mobile-nav-link'
                }
                onClick={() => setMenuOpen(false)}
              >
                <span>{link.label}</span>
                <NavArrowIcon />
              </NavLink>
            ))}
            {isLoggedIn ? (
              <>
                <div className="mobile-account-meta">
                  <strong>{user.full_name}</strong>
                  <span>{user.email}</span>
                </div>
                <button type="button" className="mobile-logout" onClick={handleLogout}>
                  Log out
                </button>
              </>
            ) : (
              <>
                <NavLink
                  to="/login"
                  className={({ isActive }) =>
                    isActive ? 'mobile-nav-link is-active' : 'mobile-nav-link'
                  }
                  onClick={() => setMenuOpen(false)}
                >
                  <span>Log in</span>
                  <NavArrowIcon />
                </NavLink>
                <NavLink
                  to="/signup"
                  className={({ isActive }) =>
                    isActive ? 'mobile-nav-link is-active' : 'mobile-nav-link'
                  }
                  onClick={() => setMenuOpen(false)}
                >
                  <span>Create an account</span>
                  <NavArrowIcon />
                </NavLink>
              </>
            )}
          </div>

          <div className="mobile-menu-ctas">
            <Link className="header-btn header-btn-primary" to="/post-scrap" onClick={() => setMenuOpen(false)}>
              Post Scrap
            </Link>
            <Link
              className="header-btn header-btn-secondary"
              to="/become-a-collector"
              onClick={() => setMenuOpen(false)}
            >
              Become a Collector
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
}
